import {UI_STATE} from 'rhino/src/components/device/detector.service';

export const STEP_NAME = {
  signIn: 'signIn',
  profile: 'profile',
  location: 'location',
  addDevice: 'addDevice',
  activateDevice: 'activateDevice',
  finish: 'finish',
};

// Exported for testing
export const ORG_TYPE = {
  PSP: 'PSP',
  Channel: 'Channel',
  HP: 'HP',
  Generic: 'Generic',
};

export const BUSINESS_UNIT = {
  LF: 'LargeFormat',
  ThreeD: 'ThreeD',
  Indigo: 'Indigo',
  Scitex: 'Scitex',
  IHPS: 'PWI',
  NONE: 'None',
};

export const INTERNAL_ID_GBU = {
  Indigo: 'Indigo',
  Latex: 'Latex',
  ThreeD: 'ThreeD',
  Designjet: 'Designjet',
  IHPS: 'IHPS',
  Scitex: 'Scitex',
};

export const DEFAULT_STARTUP_APP = {
  Channel: {
    LargeFormat: 'reseller-portal',
  },
  DEFAULT: 'home',
};

export const SHOW_WELCOME_PAGE = {
  PSP: {
    LF: false,
    ThreeD: false,
  },
  Channel: {
    LF: false,
    ThreeD: false,
  },
  DEFAULT: true,
};

export const name = 'rnStart';
export default function rnStart(
  rnClient,
  rnHttp,
  rnSession,
  $q,
  $log,
  rnInvitation,
  rnPreferences,
  rnDeviceDetector,
  rnDcs
) {
  'ngInject';

  const START_VERSION = 4;
  const STEP_DEFINITIONS = [
    {
      type: STEP_NAME.signIn,
      businessUnits: [BUSINESS_UNIT.Indigo, BUSINESS_UNIT.Scitex, BUSINESS_UNIT.IHPS, BUSINESS_UNIT.LF, BUSINESS_UNIT.ThreeD, BUSINESS_UNIT.NONE],
      orgTypes: [ORG_TYPE.PSP, ORG_TYPE.Channel],
      isPermitted() {
        return $q.resolve(true);
      },
      isComplete() {
        return $q.resolve(true);
      }
    },
    {
      type: STEP_NAME.profile,
      businessUnits: [BUSINESS_UNIT.Indigo, BUSINESS_UNIT.Scitex, BUSINESS_UNIT.IHPS, BUSINESS_UNIT.LF, BUSINESS_UNIT.ThreeD, BUSINESS_UNIT.NONE],
      orgTypes: [ORG_TYPE.PSP, ORG_TYPE.Channel, ORG_TYPE.HP],
      isPermitted() {
        return $q.resolve(true);
      },
      isComplete() {
        return getClientConfig()
          .then(clientConfig => {
            //IF we are HP in another account where we are not a member then treat step as complete
            if (_.get(clientConfig, 'managingContext.type') === 'HP') {
              return true;
            }

            let hasJobFunctions = _.get(clientConfig, 'entity.persona.myTypes', []).length > 0;

            return hasJobFunctions;
          });
      }
    },
    {
      type: STEP_NAME.location,
      businessUnits: [BUSINESS_UNIT.Indigo, BUSINESS_UNIT.Scitex, BUSINESS_UNIT.IHPS, BUSINESS_UNIT.LF, BUSINESS_UNIT.ThreeD, BUSINESS_UNIT.NONE],
      orgTypes: [ORG_TYPE.PSP, ORG_TYPE.Channel],
      isPermitted() {
        return rnHttp.get('aaa', 'organizations/sites', {params:{offset:0,limit:1000}, cache: true}).then(resp => {
          return (_.get(resp, 'data._links.add') !== undefined);
        });
      },
      isComplete() {
        // incomplete if no sites or 1 site without an address
        return rnHttp.get('aaa', 'organizations/sites', {params:{offset:0,limit:1000}, cache: true}).then(resp => {
          let sites = resp.data.sites;
          if (sites.length === 0) {
            return false;
          }
          else if (sites.length === 1) {
            if (!_.get(sites[0], 'primaryAddress.addressLine1')) {
              return false;
            }
          }

          return true;
        });
      }
    },
    {
      type: STEP_NAME.addDevice,
      businessUnits: [BUSINESS_UNIT.LF, BUSINESS_UNIT.Indigo, BUSINESS_UNIT.Scitex, BUSINESS_UNIT.IHPS, BUSINESS_UNIT.ThreeD, BUSINESS_UNIT.NONE],
      orgTypes: [ORG_TYPE.PSP],
      isPermitted() {
        let promises = [];
        promises.push(isBusinessUnit(BUSINESS_UNIT.ThreeD));
        promises.push(queryDevices());
        promises.push(canAddDevice());
        return $q.all(promises).then(([isThreeD, devicesContainer, canAddDevice]) => {
          let hasDevices = devicesContainer.total > 0;
          if (isThreeD) {
            return hasDevices && canAddDevice;
          }

          return canAddDevice;
        });
      },
      isComplete() {
        let promises = [];
        promises.push(queryDevices());
        promises.push(queryPayloads());
        promises.push(rnHttp.get('aaa', 'organizations/sites', {params:{offset:0,limit:1000}, cache: true}));
        promises.push(rnPreferences.getSingleObject('pos.gz.task', 'pos.gz.taskOut'));
        return $q.all(promises).then(([devicesContainer, payloads, sitesResp, taskData]) => {
          let hasSites = sitesResp.data.sites.length > 0;
          let hasDevices = devicesContainer.total > 0;
          let hasPayloads = payloads.length > 0;
          let hasUnAddedPayloadDevices = false;
          if (hasPayloads) {
            let payloadDevices = _.get(payloads[0], 'ext_payload.onboard.devices', []);
            _.forEach(payloadDevices, payloadDevice => {
              let foundSN = devicesContainer.devices.find(d => {
                return d.serialNumber === payloadDevice.serialNumber || d.serialNumber === _.get(taskData, 'payload.sn');
              });
              if (foundSN === undefined) {
                hasUnAddedPayloadDevices = true;
                return false;
              }
            });
          }
          let hasUnassigned = false;
          _.forEach(devicesContainer.devices, device => {
            if (hasSites && !_.get(device, 'site')) {
              hasUnassigned = true;
              return false;
            }
          });
          return hasDevices && !hasUnAddedPayloadDevices && !hasUnassigned;
        });
      },
    },
    {
      type: STEP_NAME.activateDevice,
      businessUnits: [BUSINESS_UNIT.LF, BUSINESS_UNIT.Indigo],
      orgTypes: [ORG_TYPE.PSP],
      isPermitted: canAddDevice,
      isComplete() {
        // Complete if there is at least one activated device
        // or there are no devices that can be activated
        let promises = [];
        promises.push(getIndigoDevicesToActivate());
        return $q.all(promises).then(([latexDevices, indigoDevices]) => {
          return hasActivatedDevice(latexDevices, indigoDevices) || !hasUnactivatedDevice(latexDevices, indigoDevices);
        });
      },
    },
  ];


  let deviceType;
  let ccPromise;
  let cc;

  let service = {
    getStartInfo,
    getOrgInfo,
    getDeviceType,
    getBusinessUnits,
    getRecommendedStartupApp: getDefaultStartupApp,
    skipStep: skipStep,
    finishSteps: finishSteps,
    getSkipWelcome: getSkipWelcome,
    setSkipWelcome: setSkipWelcome,
    unitTest: {
      getStepDefinitions: () => STEP_DEFINITIONS,
    },
  };

  return service;

  ////////////////////
  function getClientConfig() {
    if (cc) {
      return Promise.resolve(cc);
    }

    if (!ccPromise) {
      ccPromise = rnClient.getConfig(false).then(resp => {
        cc = resp;
        return cc;
      });
    }

    return ccPromise;
  }

  function queryDevices() {
    // No need to call the device service for this one since we just need a subset of device data.
    let config = {
      params: {
        limit: 1000
      },
      cache: true
    };
    return rnHttp.get('aaa', 'organizations/devices', config)
      .then(resp => {
        return resp.data;
      });
  }

  function queryPayloads() {
    let config = {
      cache: true
    };
    return rnHttp.get('email_service', 'invitations/payload', config).then(resp => {
      return resp.data;
    }).catch(() => {
      // Treat 401's and other issues as no payloads - this is not critical data
      return [];
    });
  }

  function queryOrg() {
    return rnHttp.get('aaa', 'organizations', { cache: true })
      .then(resp => {
        return resp.data;
      });
  }

  function getOrgInfo() {
    return $q.all({
      businessUnits: getBusinessUnits(),
      orgType: getOrgType(),
    });
  }

  function getDeviceType() {
    return deviceType;
  }

  function canAddDevice() {
    return rnDcs.getAll(0,0)
      .then(devicesContainer => {
        return _.has(devicesContainer, '_links.create');
      })
      .catch(() => {
        return false;
      });
  }

  function hasActivatedDevice(latexDevices, indigoDevices){
    //1. Check for an activated Latex
    if (latexDevices.activationStatusesWithContext.length > 0){
      let found = latexDevices.activationStatusesWithContext[0].activationStatuses;
      let hasActivatedLatex = found.some(activationStatus => {
        return activationStatus.status === 'active';
      });

      if (hasActivatedLatex) {
        return true;
      }
    }

    //2. Check for an activated Indigo
    indigoDevices = indigoDevices || [];
    let hasActivatedIndigo = indigoDevices.some(device => {
      return device.uiState === UI_STATE.activated;
    });

    return hasActivatedIndigo;
  }

  function hasUnactivatedDevice(latexDevices, indigoDevices){
    //1. Check whether latex devices are available to activate
    if (latexDevices.activationStatusesWithContext.length > 0){
      let found = latexDevices.activationStatusesWithContext[0].activationStatuses;
      let hasUnactivatedLatex = found.some(activationStatus => {
        return activationStatus.status !== 'active';
      });

      if (hasUnactivatedLatex) {
        return true;
      }
    }

    //2. Check whether Indigo devices are available to activate
    if (indigoDevices && indigoDevices.length > 0){
      return true;
    }

    return false;
  }

  function getIndigoDevicesToActivate(){
    return rnDeviceDetector.getCanActivateDevices().then(deviceList => {
      return deviceList;
    }).catch(() => {
      return [];
    });
  }

  function getBusinessUnits() {
    let businessUnits = [];
    return queryOrg()
      .then(org => {
        if (org.internalIds) {
          if (org.internalIds.length === 0) {
            businessUnits.push(BUSINESS_UNIT.NONE);
          }

          org.internalIds.forEach(internalId => {
            switch(internalId.gbu) {
              case INTERNAL_ID_GBU.Latex:
                if (businessUnits.indexOf(BUSINESS_UNIT.LF) === -1) {
                  businessUnits.push(BUSINESS_UNIT.LF);
                }
                break;
              case INTERNAL_ID_GBU.ThreeD:
                if (businessUnits.indexOf(BUSINESS_UNIT.ThreeD) === -1) {
                  businessUnits.push(BUSINESS_UNIT.ThreeD);
                }
                break;
              case INTERNAL_ID_GBU.Indigo:
                if (businessUnits.indexOf(BUSINESS_UNIT.Indigo) === -1) {
                  businessUnits.push(BUSINESS_UNIT.Indigo);
                }
                break;
              case INTERNAL_ID_GBU.Designjet:
                if (businessUnits.indexOf(BUSINESS_UNIT.LF) === -1) {
                  businessUnits.push(BUSINESS_UNIT.LF);
                }
                break;
              case INTERNAL_ID_GBU.Scitex:
                if (businessUnits.indexOf(BUSINESS_UNIT.Scitex) === -1) {
                  businessUnits.push(BUSINESS_UNIT.Scitex);
                }
                break;
              case INTERNAL_ID_GBU.IHPS:
                if (businessUnits.indexOf(BUSINESS_UNIT.IHPS) === -1) {
                  businessUnits.push(BUSINESS_UNIT.IHPS);
                }
                break;
            }
          });
        }
        else {
          businessUnits.push(BUSINESS_UNIT.NONE);
        }

        return businessUnits;
      });
  }

  function getOrgType() {
    return getClientConfig()
      .then(clientConfig => {
        return clientConfig.context.type;
      });
  }

  function getStartInfo(force = false) {
    return getClientConfig().then(clientConfig => {
      return getShortCircuit(clientConfig, force).then(shortCircuit => {
        let promises = [];
        promises.push(getApplicableSteps(shortCircuit));
        promises.push(getDefaultStartupApp(clientConfig));
        promises.push(getShowWelcomePage(clientConfig));
        return $q.all(promises).then(([steps, defaultAppId, showWelcome]) => {
          let startInfo = {
            showWelcome: showWelcome,
            defaultAppId: defaultAppId,
          };

          if (shortCircuit) {
            shortCircuitSteps(steps);
            startInfo.hasIncompleteSteps = false;
            startInfo.steps = steps;
            return startInfo;
          }
          else {
            return initializeSteps(steps).then(steps => {
              startInfo.hasIncompleteSteps = _.some(steps, {complete: false});
              startInfo.steps = steps;

              return startInfo;
            });
          }
        })
          .catch(error => {
            $log.error('Error (getStartInfo)', error);
          });
      });
    });
  }

  function getShortCircuit(clientConfig, force = false) {
    if (force) {
      return $q.resolve(false);
    }

    let orgId = clientConfig.context.id;
    return rnPreferences.getSingleObject('home', 'start.' + orgId).then(startPrefs => {
      let version = _.get(startPrefs, 'completeSteps.finish', 0);
      if (version === true || version === false) {
        return false;
      }

      return version === START_VERSION;
    });
  }

  function shortCircuitSteps(steps) {
    _.forEach(steps, step => {
      let stepDef = _.find(STEP_DEFINITIONS, { type: step.type });
      step.complete = true;
      step.optional = _.get(stepDef, 'optional', false);
    });

    steps.push({
      type: STEP_NAME.finish,
      complete: true
    });
  }

  function initializeSteps(steps) {
    let promises = [];
    steps.forEach(step => {
      let stepDef = _.find(STEP_DEFINITIONS, { type: step.type });
      promises.push(stepDef.isComplete()
        .then(complete => {
          step.complete = complete;
          step.optional = _.get(stepDef, 'optional', false);
          return step;
        })
      );
    });

    return $q.all(promises)
      .then(steps => {
        if (_.some(steps, { complete: false })) {
          // If there are any remaining steps, add a finish step as well.
          steps.push({
            type: STEP_NAME.finish,
            complete: false
          });
          cleanupSteps(steps);
        }
        else {
          // Otherwise, we need to return all steps as complete
          steps.push({
            type: STEP_NAME.finish,
            complete: true
          });
          finishSteps();
        }
        return steps;
      });
  }

  function getApplicableSteps(shortCircuit) {
    if (shortCircuit) {
      return [];
    }

    return getOrgApplicableStepsDefs()
      .then(stepDefs => {
        let promises = [];
        stepDefs.forEach(stepDef => {
          promises.push(stepDef.isPermitted()
            .then(permitted => {
              return {
                type: stepDef.type,
                permitted,
              };
            })
          );
        });

        return $q.all(promises).then(sparseSteps => {
          return _.filter(sparseSteps, { permitted: true });
        });
      })
      .catch(error => {
        console.error('Error (getApplicableSteps) - ' + error);
      });
  }

  function isBusinessUnit(businessUnit) {
    return getOrgInfo().then(orgInfo => {
      return orgInfo.businessUnits.indexOf(businessUnit) > -1;
    });
  }

  function getOrgApplicableStepsDefs() {
    return getOrgInfo().then(orgInfo => {
      let filteredStepDefs = _.filter(STEP_DEFINITIONS, stepDef => {
        if (stepDef.hasOwnProperty('orgTypes') && !_.includes(stepDef.orgTypes, orgInfo.orgType)) {
          return false;
        }

        if (stepDef.hasOwnProperty('businessUnits') && _.isEmpty(_.intersection(stepDef.businessUnits, orgInfo.businessUnits))) {
          return false;
        }

        return true;
      });

      return filteredStepDefs;
    });
  }

  function getDefaultStartupApp(clientConfig) {
    return rnPreferences.getSingleObject('app-switcher', `${clientConfig.context.type}`).then(myPref => {
      // If we already have a default preference for this account we should go there
      if (myPref && myPref.defaultAppId) {
        return myPref.defaultAppId;
      }

      return getDefaultAppId(clientConfig);
    })
      .catch(error => {
        console.error('Error (getDefaultStartupApp) - ' + error);
      });
  }

  function getRedirectAppId() {
    return queryPayloads().then(invitations => {
      let redirectAppId;
      _.forEach(invitations, invitation => {
        redirectAppId = _.get(invitations[0], 'ext_payload.onboard.redirectAppId');
        if (redirectAppId) {
          return false;
        }
      });

      return redirectAppId;
    })
      .catch(() => {});
  }

  function getDefaultAppId(clientConfig) {
    let promises = [];
    promises.push(getRedirectAppId());
    promises.push(getBusinessUnits());
    return $q.all(promises).then(([redirectAppId, businessUnits]) => {
      let defaultAppId;
      if (redirectAppId) {
        defaultAppId = redirectAppId;
      }
      else if (businessUnits.length === 1) {
        let bu = businessUnits[0];
        defaultAppId = _.get(DEFAULT_STARTUP_APP, [clientConfig.context.type, bu], DEFAULT_STARTUP_APP.DEFAULT);
      }
      else {
        defaultAppId = DEFAULT_STARTUP_APP.DEFAULT;
      }

      return rnPreferences.setSingle('app-switcher', `defaultAppId.${clientConfig.context.id}`, defaultAppId).then(() => {
        return defaultAppId;
      });
    });
  }

  function getShowWelcomePage(clientConfig) {
    let promises = [];
    promises.push(getBusinessUnits());
    promises.push(getSkipWelcome());
    return $q.all(promises)
      .then(([businessUnits, skipWelcome]) => {
        if (clientConfig.context.type === ORG_TYPE.Generic) {
          return false;
        }

        let showWelcome = SHOW_WELCOME_PAGE.DEFAULT;
        if (businessUnits.length === 1) {
          let bu = businessUnits[0];
          showWelcome = _.get(SHOW_WELCOME_PAGE, clientConfig.context.type + '.' + bu, SHOW_WELCOME_PAGE.DEFAULT);
        }

        if (showWelcome) {
          showWelcome = !skipWelcome;
        }

        return showWelcome;
      })
      .catch(error => {
        console.error('Error (getShowWelcomePage) - ' + error);
      });
  }

  function getSkipWelcome() {
    return getClientConfig().then(clientConfig => {
      let orgId = clientConfig.context.id;
      let promises = [];
      promises.push(rnPreferences.getSingleObject('home', 'start'));
      promises.push(rnPreferences.getSingleObject('home', 'start.' + orgId));
      return $q.all(promises).then(([globalPref, orgPref]) =>  {
        let skip = _.get(globalPref, 'skipWelcome');
        if (!skip) {
          skip = _.get(orgPref, 'skipWelcome', false);
        }
        return skip;
      });
    });
  }

  function setSkipWelcome(skip) {
    return getClientConfig().then(clientConfig => {
      let orgId = clientConfig.context.id;
      return rnPreferences.getSingleObject('home', 'start').then(p => {
        if (!p) {
          p = {};
        }
        p.skipWelcome = skip;
        return rnPreferences.setSingleObject('home', 'start', p);
      });
    });
  }

  function skipStep(step) {
    if (!step.optional) {
      return $q.resolve(false);
    }

    return getClientConfig().then(clientConfig => {
      let orgId = clientConfig.context.id;
      return rnPreferences.getSingleObject('home', 'start.' + orgId).then(p => {
        if (!p) {
          p = {};
        }

        _.set(p, 'completeSteps.' + step.type, START_VERSION);
        step.complete = true;

        return rnPreferences.setSingleObject('home', 'start.' + orgId, p);
      });
    });
  }

  function finishSteps() {
    return getClientConfig().then(clientConfig => {
      let orgId = clientConfig.context.id;
      return rnPreferences.getSingleObject('home', 'start.' + orgId).then(p => {
        if (!p) {
          p = {};
        }

        _.set(p, 'completeSteps.' + STEP_NAME.finish, START_VERSION);
        return rnPreferences.setSingleObject('home', 'start.' + orgId, p);
      });
    });
  }

  function cleanupSteps(steps) {
    let markAsIncomplete = false;
    for (let i=0; i < steps.length; i++) {
      if (!markAsIncomplete) {
        if (!steps[i].complete) {
          markAsIncomplete = true;
        }
      }
      else {
        steps[i].complete = false;
      }
    }
  }
}
